import {useState, useRef, useEffect, useCallback, useMemo} from 'react';
import {
  StyleSheet,
  View,
  Pressable,
  Platform,
  Modal,
  ScrollView,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import FirebaseAnalytic from '../../../../nativeModules/FirebaseAnalytic';
import Settings from 'src/Settings';
import BottomSheet, {BottomSheetScrollView} from '@gorhom/bottom-sheet';
import Styles from 'src/components/Styles';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../../i18n/HOC/withGlobalize';
import {BottomSheetStateEnum} from 'src/constants/BottomSheetStateEnum';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import Localized from 'src/constants/AppStrings';
import {LocationType} from 'src/types/Location';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
import Autolink from 'react-native-autolink';
import AVText from 'src/components/elements/AVText';
import {bodyFontSize} from 'src/components/elements/layoutDescriptor/layout-redesign';
import {getDescriber} from './descriptor/DescriptorType';
import {useDynamicSnapPoints} from 'src/hooks/useDynamicSnapPoints';

type DisclaimerScreenProps = WithGlobalizeProps & {
  location: LocationType;
  getRouteFromPickupLocations: (
    location: LocationType,
    autoAdvanced?: boolean,
  ) => Promise<{
    name: AppRoutes.NewPickupLocation | AppRoutes.NewPickupTime;
    availableTimes: any;
    error: boolean;
  }>;
  autoAdvanced?: boolean;
};

const {mainFontFamily} = getLayoutDescriber();
const overrideStyle = getDescriber().disclaimerScreenStyle();

const DisclaimerScreen: React.FC<DisclaimerScreenProps> = (props) => {
  const [modalVisible, setModalVisible] = useState(Platform.OS === 'web');
  const bottomSheetRef = useRef<BottomSheet>(null);

  const bottomSheetCustomStates = useMemo(
    () => ({
      [-1]: BottomSheetStateEnum.FULLY_CLOSED,
      0: BottomSheetStateEnum.FULLY_OPEN,
      999: BottomSheetStateEnum.NOT_READY,
    }),
    [],
  );

  const {snapPoints, handleContentLayout} = useDynamicSnapPoints();

  const bottomSheetCurrentState = useRef<BottomSheetStateEnum | undefined>(
    undefined,
  );

  useEffect(() => {
    FirebaseAnalytic.trackEvent('componentDidMount', 'DisclaimerScreen', {
      ...props,
    });
    if (bottomSheetRef.current) {
      bottomSheetRef.current.close();
    }
  }, [props]);

  const trackOnCloseEvent = () => {
    FirebaseAnalytic.trackEvent('onClose', 'DisclaimerScreen', {
      ...props,
      navigate: AppRoutes.Main,
    });
  };

  const handleContinue = async () => {
    FirebaseAnalytic.trackEvent(
      'DisclaimerContinue',
      'DisclaimerScreen: Continue',
      {
        ...props,
      },
    );
    if (Platform.OS !== 'web') {
      NavActions.pop();
    } else {
      const routeInfo = await props.getRouteFromPickupLocations(
        props.location,
        props.autoAdvanced,
      );
      if (!routeInfo.error) {
        FirebaseAnalytic.trackEvent(
          'DisclaimerContinue',
          'DisclaimerScreen: Continue',
          {
            ...props,
          },
        );
        NavActions.navigate(
          routeInfo.name as AppRoutes,
          {
            ...props,
            availableTimes: routeInfo.availableTimes,
          },
          'machines-pickup',
        );
      }
    }
  };

  const handleNavigation = () => {
    if (Settings.isNewUI()) {
      NavActions.pop();
    } else {
      NavActions.navigate(AppRoutes.Main);
    }
  };

  const onClose = useCallback(() => {
    if (Platform.OS === 'web') {
      setModalVisible(false);
      trackOnCloseEvent();
      handleNavigation();
    } else {
      if (!bottomSheetRef.current) {
        return;
      }
      trackOnCloseEvent();
      handleNavigation();
    }
  }, []);

  const handleSheetChange = (index: number) => {
    bottomSheetCurrentState.current = bottomSheetCustomStates[index];
    if (index === -1) {
      onClose();
    }
  };
  const renderInner = () => (
    <>
      <View style={styles.titleHead}>
        <AVText
          style={styles.titleHeadText}
          accessibilityLabel={Localized.Labels.disclaimer}
        >
          {Localized.Labels.disclaimer}
        </AVText>
      </View>
      <Autolink
        accessible={true}
        accessibilityLabel={props.location.onlineOrderConfig.disclaimer}
        linkProps={{
          maxFontSizeMultiplier: Styles.FontSizeMultiplier.maxfm11,
        }}
        accessibilityRole="text"
        aria-label={props.location.onlineOrderConfig.disclaimer}
        style={styles.disclaimerText}
        text={props.location.onlineOrderConfig.disclaimer}
      />
      <View style={styles.buttonWrapper}>
        <RoundedAppButton
          buttonType={RoundedAppButtonType.Solid}
          onPress={handleContinue}
          accessibilityLabelValue="Continue to pickup locations"
          aria-label="Continue to pickup locations"
          titleText={Localized.Buttons.continue}
          buttonContainerStyle={overrideStyle.buttonContainer}
          buttonViewStyle={overrideStyle.buttonView}
          textStyle={styles.buttonText}
          testID="qa_ContinueToPickupLocations_btn "
        />
      </View>
    </>
  );

  const renderWebModal = () => (
    <Modal
      animationType="fade"
      transparent={true}
      visible={modalVisible}
      onRequestClose={onClose}
    >
      <View style={styles.modalBackdrop}>
        <ScrollView style={styles.modalContainer}>{renderInner()}</ScrollView>
      </View>
    </Modal>
  );

  const renderBottomSheet = () => (
    <BottomSheet
      ref={bottomSheetRef}
      snapPoints={snapPoints}
      maxDynamicContentSize={450}
      enableHandlePanningGesture={true}
      enablePanDownToClose={true}
      onChange={handleSheetChange}
      style={styles.bottomSheet}
      index={0}
      enableContentPanningGesture={true}
    >
      <BottomSheetScrollView
        onLayout={handleContentLayout}
        contentContainerStyle={styles.content}
      >
        {renderInner()}
      </BottomSheetScrollView>
    </BottomSheet>
  );

  return (
    <View style={styles.container} testID="pressable-element">
      <Pressable
        nativeID="Btn-pressable-element"
        style={styles.contentParentView}
        onPress={() => {
          if (bottomSheetCurrentState.current === undefined) {
            NavActions.pop();
          } else if (bottomSheetRef.current) {
            bottomSheetRef.current.forceClose();
          }
        }}
      />
      {Platform.OS === 'web' ? renderWebModal() : renderBottomSheet()}
    </View>
  );
};

const styles = StyleSheet.create({
  titleHead: {
    justifyContent: 'center',
    flexDirection: 'row',
  },
  titleHeadText: {
    fontWeight: 'bold',
    fontSize: bodyFontSize(),
  },
  buttonWrapper: {
    justifyContent: 'center',
    flexDirection: 'row',
  },
  buttonText: {
    fontFamily: mainFontFamily(),
    fontSize: bodyFontSize(),
    fontWeight: '700',
  },
  content: {
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    paddingBottom: Styles.Spacing.m4 + Styles.Spacing.m3,
  },
  disclaimerText: {
    textAlign: 'center',
    marginBottom: Styles.Spacing.m4 + Styles.Spacing.m2,
    color: Styles.darkColor,
    fontSize: bodyFontSize(),
  },

  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    padding: Styles.Spacing.m3,
  },
  contentParentView: {
    width: '100%',
    height: '60%',
    backgroundColor: 'transparent',
  },
  bottomSheet: {
    borderRadius: 32,
    overflow: 'hidden',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: Styles.Spacing.m3,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 600,
    flexGrow: 0,
    maxHeight: '80%',
    backgroundColor: Styles.Colors.PayNew.white01,
    borderRadius: Styles.Spacing.m3 + Styles.Spacing.m2,
    padding: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
});

export default withForwardedNavigationParams()(withGlobalize(DisclaimerScreen));
